import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  uploadCsvFile,
  startImportJob,
  fetchImportJobs,
  fetchImportJob,
  startExportJob,
  fetchExportJobs,
  downloadExportFile,
  validateImportData,
  fetchBulkOperationStats,
  type ImportJob,
  type ExportJob,
  type ImportMapping,
  type Application
} from '../../services/api/bulkDataApi';
import { useToast } from '../../contexts/ToastContext';

type ViewMode = 'import' | 'export' | 'history';

const BulkDataManager: React.FC = () => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { success, error } = useToast();

  // State
  const [viewMode, setViewMode] = useState<ViewMode>('import');
  const [selectedApp, setSelectedApp] = useState<Application>('cybersweep');
  const [entityType, setEntityType] = useState('client');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [csvPreview, setCsvPreview] = useState<any>(null);
  const [mappings, setMappings] = useState<ImportMapping[]>([]);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState<any[]>([]);

  // Fetch stats
  const { data: statsResponse } = useQuery({
    queryKey: ['bulk-operation-stats'],
    queryFn: fetchBulkOperationStats,
    staleTime: 60 * 1000
  });

  const stats = statsResponse?.data;

  // Fetch import jobs
  const { data: importJobsResponse, isLoading: importJobsLoading } = useQuery({
    queryKey: ['import-jobs', selectedApp],
    queryFn: () => fetchImportJobs({ application: selectedApp }),
    staleTime: 30 * 1000,
    enabled: viewMode === 'import' || viewMode === 'history'
  });

  const importJobs = importJobsResponse?.data || [];

  // Fetch export jobs
  const { data: exportJobsResponse, isLoading: exportJobsLoading } = useQuery({
    queryKey: ['export-jobs', selectedApp],
    queryFn: () => fetchExportJobs({ application: selectedApp }),
    staleTime: 30 * 1000,
    enabled: viewMode === 'export' || viewMode === 'history'
  });

  const exportJobs = exportJobsResponse?.data || [];

  // Mutations
  const uploadMutation = useMutation({
    mutationFn: ({ file, app, entity }: { file: File; app: Application; entity: string }) =>
      uploadCsvFile(file, app, entity),
    onSuccess: (response) => {
      setCsvPreview(response.data);
      setMappings(response.data.suggestedMappings);
      setShowMappingModal(true);
    }
  });

  const importMutation = useMutation({
    mutationFn: ({ file, app, entity, mappings }: any) =>
      startImportJob(file, app, entity, mappings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['import-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['bulk-operation-stats'] });
      setUploadedFile(null);
      setCsvPreview(null);
      setMappings([]);
      setShowMappingModal(false);
    }
  });

  const exportMutation = useMutation({
    mutationFn: ({ app, entity, columns }: any) =>
      startExportJob(app, entity, columns),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['export-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['bulk-operation-stats'] });
    }
  });

  const validateMutation = useMutation({
    mutationFn: ({ file, app, entity, mappings }: any) =>
      validateImportData(file, app, entity, mappings),
    onSuccess: (response) => {
      setValidationErrors(response.data.errors);
      if (response.data.isValid) {
        success('Validation passed!', 'Ready to import');
      } else {
        error('Validation failed', `Found ${response.data.errors.length} error(s). Please review.`);
      }
    }
  });

  // Handlers
  const handleFileSelect = (file: File) => {
    if (!file.name.endsWith('.csv')) {
      error('Invalid file type', 'Please upload a CSV file');
      return;
    }

    setUploadedFile(file);
    uploadMutation.mutate({
      file,
      app: selectedApp,
      entity: entityType
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleStartImport = () => {
    if (!uploadedFile) return;

    if (window.confirm(`Import ${csvPreview.rowCount} rows into ${entityType}?`)) {
      importMutation.mutate({
        file: uploadedFile,
        app: selectedApp,
        entity: entityType,
        mappings
      });
    }
  };

  const handleValidate = () => {
    if (!uploadedFile) return;

    validateMutation.mutate({
      file: uploadedFile,
      app: selectedApp,
      entity: entityType,
      mappings
    });
  };

  const handleStartExport = () => {
    const columns = window.prompt('Enter field names to export (comma-separated):');
    if (!columns) return;

    exportMutation.mutate({
      app: selectedApp,
      entity: entityType,
      columns: columns.split(',').map(c => c.trim())
    });
  };

  const handleDownloadExport = (jobId: string) => {
    downloadExportFile(jobId);
  };

  return (
    <div className="bulk-data-manager">
      {/* Developer Indicator */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.5rem 1rem',
          background: '#E3F2FD',
          borderRadius: '4px',
          fontSize: '0.875rem',
          color: '#1565C0',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span>ℹ️</span>
          <span>Showing mock data. Connect backend to persist changes.</span>
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ marginBottom: '0.5rem', color: 'var(--text-primary)' }}>Bulk Data Operations</h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
          Import and export data, manage bulk operations, and track job history
        </p>
      </div>

      {/* Statistics */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{
            padding: '1rem',
            background: 'var(--background-secondary)',
            borderRadius: '6px',
            border: '1px solid var(--border-color)'
          }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
              Total Imports
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
              {stats.totalImports}
            </div>
          </div>

          <div style={{
            padding: '1rem',
            background: 'var(--background-secondary)',
            borderRadius: '6px',
            border: '1px solid var(--border-color)'
          }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
              Total Exports
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
              {stats.totalExports}
            </div>
          </div>

          <div style={{
            padding: '1rem',
            background: 'rgba(33, 150, 243, 0.15)',
            borderRadius: '6px',
            border: '1px solid #2196F3'
          }}>
            <div style={{ fontSize: '0.75rem', color: '#2196F3', marginBottom: '0.25rem' }}>
              Recent Jobs (24h)
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#2196F3' }}>
              {stats.recentJobs}
            </div>
          </div>

          <div style={{
            padding: '1rem',
            background: stats.failedJobs > 0 ? 'rgba(244, 67, 54, 0.15)' : 'var(--background-secondary)',
            borderRadius: '6px',
            border: `1px solid ${stats.failedJobs > 0 ? '#F44336' : 'var(--border-color)'}`
          }}>
            <div style={{
              fontSize: '0.75rem',
              color: stats.failedJobs > 0 ? '#F44336' : 'var(--text-secondary)',
              marginBottom: '0.25rem'
            }}>
              Failed Jobs
            </div>
            <div style={{
              fontSize: '1.5rem',
              fontWeight: 600,
              color: stats.failedJobs > 0 ? '#F44336' : 'var(--text-primary)'
            }}>
              {stats.failedJobs}
            </div>
          </div>
        </div>
      )}

      {/* View Mode Tabs */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1.5rem',
        borderBottom: '2px solid var(--border-color)'
      }}>
        {[
          { id: 'import' as const, label: '📤 Import Data' },
          { id: 'export' as const, label: '📥 Export Data' },
          { id: 'history' as const, label: '📋 History' }
        ].map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${viewMode === tab.id ? 'active' : ''}`}
            onClick={() => setViewMode(tab.id)}
            style={{
              padding: '0.75rem 1.5rem',
              background: viewMode === tab.id ? 'var(--background-primary)' : 'transparent',
              color: 'var(--text-primary)',
              border: 'none',
              borderBottom: viewMode === tab.id ? '3px solid var(--primary-color)' : '2px solid transparent',
              borderRadius: '6px 6px 0 0',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: viewMode === tab.id ? 700 : 600,
              transition: 'all 0.2s'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Application & Entity Selection */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
            Application
          </label>
          <select
            value={selectedApp}
            onChange={(e) => setSelectedApp(e.target.value as Application)}
            style={{
              width: '100%',
              padding: '0.5rem 0.75rem',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              fontSize: '0.875rem',
              color: 'var(--text-primary)',
              backgroundColor: 'var(--background-primary)'
            }}
          >
            <option value="cybersweep">CyberSweep</option>
            <option value="credit-management">Credit Management</option>
            <option value="credit-block">Credit Block</option>
            <option value="regal-main">Regal Main</option>
          </select>
        </div>

        <div style={{ flex: 1 }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
            Entity Type
          </label>
          <select
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem 0.75rem',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              fontSize: '0.875rem',
              color: 'var(--text-primary)',
              backgroundColor: 'var(--background-primary)'
            }}
          >
            <option value="client">Client</option>
            <option value="case">Case</option>
            <option value="audit">Audit</option>
            <option value="contact">Contact</option>
          </select>
        </div>
      </div>

      {/* Import View */}
      {viewMode === 'import' && (
        <div>
          {/* File Upload Area */}
          {!uploadedFile && (
            <div
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              style={{
                border: '2px dashed var(--border-color)',
                borderRadius: '8px',
                padding: '3rem',
                textAlign: 'center',
                background: 'var(--background-secondary)',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.3 }}>📁</div>
              <h4 style={{ marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
                Drop CSV file here or click to browse
              </h4>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                Supports CSV files for Salesforce data import
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                style={{ display: 'none' }}
              />
            </div>
          )}

          {/* CSV Preview */}
          {csvPreview && (
            <div style={{
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              padding: '1.5rem',
              background: 'var(--background-secondary)'
            }}>
              <div style={{ marginBottom: '1rem' }}>
                <h4 style={{ marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
                  File Preview: {uploadedFile?.name}
                </h4>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  {csvPreview.rowCount} rows, {csvPreview.headers.length} columns
                </p>
              </div>

              {/* Preview Table */}
              <div style={{ overflowX: 'auto', marginBottom: '1rem' }}>
                <table style={{ width: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
                  <thead style={{ background: 'var(--background-primary)' }}>
                    <tr>
                      {csvPreview.headers.map((header: string, i: number) => (
                        <th key={i} style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {csvPreview.preview.slice(0, 5).map((row: any, i: number) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        {csvPreview.headers.map((header: string, j: number) => (
                          <td key={j} style={{ padding: '0.5rem', color: 'var(--text-primary)' }}>
                            {row[header]}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => setShowMappingModal(true)}
                  disabled={uploadMutation.isPending}
                  style={{
                    padding: '0.5rem 1rem',
                    background: 'var(--primary-color)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: uploadMutation.isPending ? 'not-allowed' : 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }}
                >
                  Configure Field Mapping
                </button>
                <button
                  onClick={handleValidate}
                  disabled={validateMutation.isPending || mappings.length === 0}
                  style={{
                    padding: '0.5rem 1rem',
                    background: 'var(--background-primary)',
                    color: 'var(--primary-color)',
                    border: '1px solid var(--primary-color)',
                    borderRadius: '6px',
                    cursor: (validateMutation.isPending || mappings.length === 0) ? 'not-allowed' : 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }}
                >
                  {validateMutation.isPending ? 'Validating...' : 'Validate Data'}
                </button>
                <button
                  onClick={() => {
                    setUploadedFile(null);
                    setCsvPreview(null);
                    setMappings([]);
                  }}
                  style={{
                    padding: '0.5rem 1rem',
                    background: 'var(--background-primary)',
                    color: 'var(--text-secondary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Recent Import Jobs */}
          <div style={{ marginTop: '2rem' }}>
            <h4 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>Recent Import Jobs</h4>
            {importJobsLoading ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading...</div>
            ) : importJobs.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                No import jobs yet
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {importJobs.slice(0, 5).map(job => (
                  <JobCard key={job.id} job={job} type="import" />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Export View */}
      {viewMode === 'export' && (
        <div>
          <div style={{ marginBottom: '1.5rem' }}>
            <button
              onClick={handleStartExport}
              disabled={exportMutation.isPending}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'var(--primary-color)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: exportMutation.isPending ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: 500
              }}
            >
              {exportMutation.isPending ? 'Starting Export...' : '📥 Start New Export'}
            </button>
          </div>

          <h4 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>Export Jobs</h4>
          {exportJobsLoading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading...</div>
          ) : exportJobs.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
              No export jobs yet
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {exportJobs.map(job => (
                <ExportJobCard
                  key={job.id}
                  job={job}
                  onDownload={() => handleDownloadExport(job.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* History View */}
      {viewMode === 'history' && (
        <div>
          <h4 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>All Jobs</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {[...importJobs, ...exportJobs]
              .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())
              .map(job => (
                'fileName' in job && 'totalRows' in job && 'successRows' in job ? (
                  <JobCard key={job.id} job={job as ImportJob} type="import" />
                ) : (
                  <ExportJobCard
                    key={job.id}
                    job={job as ExportJob}
                    onDownload={() => handleDownloadExport(job.id)}
                  />
                )
              ))}
          </div>
        </div>
      )}

      {/* Field Mapping Modal */}
      {showMappingModal && csvPreview && (
        <FieldMappingModal
          csvHeaders={csvPreview.headers}
          suggestedMappings={mappings}
          onClose={() => setShowMappingModal(false)}
          onSave={(newMappings) => {
            setMappings(newMappings);
            setShowMappingModal(false);
          }}
          onImport={() => {
            setShowMappingModal(false);
            handleStartImport();
          }}
        />
      )}
    </div>
  );
};

// Job Card Component
const JobCard: React.FC<{ job: ImportJob; type: 'import' }> = ({ job }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return { bg: 'rgba(76, 175, 80, 0.15)', color: '#4CAF50', border: '#4CAF50' };
      case 'processing': return { bg: 'rgba(33, 150, 243, 0.15)', color: '#2196F3', border: '#2196F3' };
      case 'failed': return { bg: 'rgba(244, 67, 54, 0.15)', color: '#F44336', border: '#F44336' };
      default: return { bg: 'rgba(255, 152, 0, 0.15)', color: '#FF9800', border: '#FF9800' };
    }
  };

  const colors = getStatusColor(job.status);

  return (
    <div style={{
      padding: '1rem',
      background: 'var(--background-secondary)',
      border: '1px solid var(--border-color)',
      borderRadius: '6px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, marginBottom: '0.25rem', color: 'var(--text-primary)' }}>
          {job.fileName}
        </div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
          {job.entityType} • {job.totalRows} rows • Started {new Date(job.startedAt).toLocaleString()}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <div style={{ textAlign: 'right', fontSize: '0.875rem' }}>
          <div style={{ color: '#4CAF50', fontWeight: 600 }}>{job.successRows} success</div>
          {job.failedRows > 0 && (
            <div style={{ color: '#F44336', fontWeight: 600 }}>{job.failedRows} failed</div>
          )}
        </div>

        <span style={{
          padding: '0.25rem 0.75rem',
          background: colors.bg,
          color: colors.color,
          border: `1px solid ${colors.border}`,
          borderRadius: '4px',
          fontSize: '0.75rem',
          fontWeight: 600,
          textTransform: 'uppercase'
        }}>
          {job.status}
        </span>
      </div>
    </div>
  );
};

// Export Job Card Component
const ExportJobCard: React.FC<{ job: ExportJob; onDownload: () => void }> = ({ job, onDownload }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return { bg: 'rgba(76, 175, 80, 0.15)', color: '#4CAF50', border: '#4CAF50' };
      case 'processing': return { bg: 'rgba(33, 150, 243, 0.15)', color: '#2196F3', border: '#2196F3' };
      case 'failed': return { bg: 'rgba(244, 67, 54, 0.15)', color: '#F44336', border: '#F44336' };
      default: return { bg: 'rgba(255, 152, 0, 0.15)', color: '#FF9800', border: '#FF9800' };
    }
  };

  const colors = getStatusColor(job.status);

  return (
    <div style={{
      padding: '1rem',
      background: 'var(--background-secondary)',
      border: '1px solid var(--border-color)',
      borderRadius: '6px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, marginBottom: '0.25rem', color: 'var(--text-primary)' }}>
          {job.fileName}
        </div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
          {job.entityType} • {job.totalRows} rows • {job.columns.length} columns • Started {new Date(job.startedAt).toLocaleString()}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        {job.status === 'completed' && (
          <button
            onClick={onDownload}
            style={{
              padding: '0.375rem 0.75rem',
              background: 'var(--primary-color)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.75rem',
              fontWeight: 500
            }}
          >
            ⬇️ Download
          </button>
        )}

        <span style={{
          padding: '0.25rem 0.75rem',
          background: colors.bg,
          color: colors.color,
          border: `1px solid ${colors.border}`,
          borderRadius: '4px',
          fontSize: '0.75rem',
          fontWeight: 600,
          textTransform: 'uppercase'
        }}>
          {job.status}
        </span>
      </div>
    </div>
  );
};

// Field Mapping Modal Component
const FieldMappingModal: React.FC<{
  csvHeaders: string[];
  suggestedMappings: ImportMapping[];
  onClose: () => void;
  onSave: (mappings: ImportMapping[]) => void;
  onImport: () => void;
}> = ({ csvHeaders, suggestedMappings, onClose, onSave, onImport }) => {
  const [mappings, setMappings] = useState<ImportMapping[]>(suggestedMappings);

  const updateMapping = (index: number, field: keyof ImportMapping, value: any) => {
    const newMappings = [...mappings];
    newMappings[index] = { ...newMappings[index], [field]: value };
    setMappings(newMappings);
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '900px',
          maxHeight: '90vh',
          overflowY: 'auto',
          background: 'var(--background-primary)',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          width: '90%'
        }}
      >
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, color: 'var(--text-primary)' }}>Configure Field Mapping</h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: 'var(--text-secondary)'
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ padding: '1.5rem' }}>
          <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
            Map CSV columns to system fields. Suggested mappings are pre-filled based on column names.
          </p>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
              <thead style={{ background: 'var(--background-secondary)' }}>
                <tr>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--text-primary)' }}>CSV Column</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--text-primary)' }}>System Field</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--text-primary)' }}>Field Label</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center', color: 'var(--text-primary)' }}>Required</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', color: 'var(--text-primary)' }}>Transform</th>
                </tr>
              </thead>
              <tbody>
                {mappings.map((mapping, index) => (
                  <tr key={index} style={{ borderTop: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '0.75rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                      {mapping.csvColumn}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <input
                        type="text"
                        value={mapping.fieldName}
                        onChange={(e) => updateMapping(index, 'fieldName', e.target.value)}
                        placeholder="e.g., clientName__c"
                        style={{
                          width: '100%',
                          padding: '0.375rem 0.5rem',
                          border: '1px solid var(--border-color)',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          color: 'var(--text-primary)',
                          backgroundColor: 'var(--background-primary)'
                        }}
                      />
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <input
                        type="text"
                        value={mapping.fieldLabel}
                        onChange={(e) => updateMapping(index, 'fieldLabel', e.target.value)}
                        placeholder="e.g., Client Name"
                        style={{
                          width: '100%',
                          padding: '0.375rem 0.5rem',
                          border: '1px solid var(--border-color)',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          color: 'var(--text-primary)',
                          backgroundColor: 'var(--background-primary)'
                        }}
                      />
                    </td>
                    <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={mapping.required}
                        onChange={(e) => updateMapping(index, 'required', e.target.checked)}
                      />
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <select
                        value={mapping.transform || ''}
                        onChange={(e) => updateMapping(index, 'transform', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '0.375rem 0.5rem',
                          border: '1px solid var(--border-color)',
                          borderRadius: '4px',
                          fontSize: '0.875rem',
                          color: 'var(--text-primary)',
                          backgroundColor: 'var(--background-primary)'
                        }}
                      >
                        <option value="">None</option>
                        <option value="uppercase">UPPERCASE</option>
                        <option value="lowercase">lowercase</option>
                        <option value="trim">Trim whitespace</option>
                        <option value="capitalize">Capitalize</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{
          padding: '1.5rem',
          borderTop: '1px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '0.5rem'
        }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '0.5rem 1rem',
              background: 'var(--background-primary)',
              color: 'var(--text-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave(mappings)}
            style={{
              padding: '0.5rem 1rem',
              background: 'var(--background-primary)',
              color: 'var(--primary-color)',
              border: '1px solid var(--primary-color)',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500
            }}
          >
            Save Mapping
          </button>
          <button
            type="button"
            onClick={onImport}
            style={{
              padding: '0.5rem 1rem',
              background: 'var(--primary-color)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500
            }}
          >
            Start Import
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkDataManager;
